import { CSSProperties, PropsWithChildren, createContext, useContext, useEffect, useRef, useState } from 'react';

import { styled } from '@ingrid/luterkort-react';

interface ContextType {
  containerRef: React.RefObject<HTMLDivElement>;
  setBottomHeight: (height: number) => void;
  bottomVisible: boolean;
  bottomHeight: number;
}

const Context = createContext<ContextType | null>(null);

type RootProps = PropsWithChildren & {
  initialBottomHeight?: number;
  bottomVisible?: boolean;
  style?: CSSProperties;
};

type PaneProps = PropsWithChildren & {
  style?: CSSProperties;
};

function Root(props: RootProps) {
  const { style, children, bottomVisible = true, initialBottomHeight = 200 } = props;
  const [bottomHeight, setBottomHeight] = useState(initialBottomHeight);
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Context.Provider value={{ bottomHeight, bottomVisible, containerRef, setBottomHeight }}>
      <div
        ref={containerRef}
        style={{
          flexDirection: 'column',
          display: 'flex',
          height: '100%',
          width: '100%',
          ...style,
        }}
      >
        {children}
      </div>
    </Context.Provider>
  );
}

function TopPane({ children, style }: PaneProps) {
  return <div style={{ flex: 1, overflow: 'auto', ...style }}>{children}</div>;
}

function BottomPane({ children, style }: PaneProps) {
  const { bottomHeight, bottomVisible } = useContext(Context)!;

  if (!bottomVisible) {
    return false;
  }

  return <div style={{ height: bottomHeight, overflow: 'auto', ...style }}>{children}</div>;
}

const HandleElement = styled('div', {
  background: '$grey-e9',
  cursor: 'row-resize',
  height: '2px',

  variants: {
    mode: {
      dark: {
        background: '$grey-33',
      },
    },
  },
});

function Handle() {
  const { containerRef, bottomVisible, setBottomHeight } = useContext(Context)!;
  const isDraggingRef = useRef(false);

  const startDragging = () => {
    isDraggingRef.current = true;
  };

  const stopDragging = () => {
    isDraggingRef.current = false;
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDraggingRef.current || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const newHeight = rect.bottom - e.clientY;

    setBottomHeight(Math.max(50, newHeight)); // min height = 50
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopDragging);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopDragging);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!bottomVisible) {
    return false;
  }

  return <HandleElement onMouseDown={startDragging} />;
}

export const VerticalSplit = {
  Root,
  TopPane,
  BottomPane,
  Handle,
};
